import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Package, TrendingUp, Users } from 'lucide-react';
import './MetricCard.css';

/**
 * MetricCard — the CRM's one KPI tile (spec §3.4; replaces the 19 per-page tile families).
 *
 *   <MetricCard title="O'quvchilar" value={128} icon={<Users />} tone="success" change="shu oy" />
 *   <MetricCard title="Daromad" value="2.6 mln so'm" valueTitle="2 593 000 so'm" icon="sales" />
 *   <MetricCard title="Xavf ostida" value={3} icon={<ShieldAlert />} tone="danger" to="/students?risk=high" />
 *   <MetricCard loading />
 *
 * Anatomy: a section card; a top row with the label (13/500 muted, wraps, never truncates) and
 * an icon chip in a tone; the value (30/700 heading, tabular numerals, always the text colour);
 * one supporting line (`change`) or a trend pill pinned to the bottom so a row shares a baseline.
 * The chip is drawn only when `icon` is passed. Never a coloured accent strip, a radial wash,
 * a coloured value or an invented sparkline.
 *
 * The value never wraps or splits: it is one line whose size fits the tile (30px, 26 on phones,
 * down to 16px for "1 497 000 so'm" in a narrow column). The CSS reads the text's estimated width
 * in em from `--em` (worked out from the FINAL value, so the size does not wobble during the
 * count-up) and the tile width from its own container (`.metric-value-box`).
 *
 * Props
 *  - title, value, change (hint), trend ({ delta: '+12%', direction: 'up' | 'down' }), icon
 *    (element or one of 'users' | 'orders' | 'sales' | 'pending'), loading
 *  - tone: 'brand' | 'success' | 'info' | 'coral' | 'purple' | 'teal' | 'danger' | 'warning' | 'neutral'
 *    (the legacy `variant` default | primary | success | danger | info still maps onto it)
 *  - look: 'card' (default) | 'tinted' (the dashboard top row: tone-soft fill, icon on a white chip)
 *  - compact: a one-row tile (icon on the left, label over value, about 80px tall) for a KPI strip
 *    that must leave room for a pane below it (Support); the default tile is about 120px
 *  - valueTitle: the full amount for the native tooltip when `value` is compact
 *  - to | href | onClick: makes the tile interactive (lifts on hover); `active` marks the selected filter
 *  - countUp: false to skip the one-time count-up
 */

const TONES = new Set(['brand', 'success', 'info', 'coral', 'purple', 'teal', 'danger', 'warning', 'neutral']);
const VARIANT_TONE = { default: 'brand', primary: 'brand', success: 'success', danger: 'danger', info: 'info' };

const STRING_ICONS = { users: Users, orders: Package, sales: TrendingUp, pending: Clock };

function MetricCard({
  title,
  value,
  change,
  icon,
  trend,
  loading = false,
  tone,
  variant,
  look = 'card',
  compact = false,
  valueTitle,
  to,
  href,
  onClick,
  active = false,
  countUp = true,
  className = '',
}) {
  const display = useCountUp(value, { enabled: countUp && !loading });
  const resolvedTone = TONES.has(tone) ? tone : (VARIANT_TONE[variant] || 'brand');
  const tinted = look === 'tinted';

  if (loading) {
    return (
      <div className={`metric-card mc-skel${compact ? ' is-compact' : ''} ${className}`.trim()} aria-busy="true">
        <div className="metric-head">
          <div className="ui-skel mc-skel-line short metric-title" />
          <div className="ui-skel metric-icon" />
        </div>
        <div className="ui-skel mc-skel-line big metric-value-box" />
        <div className="metric-foot">
          <div className="ui-skel mc-skel-line short" />
        </div>
      </div>
    );
  }

  const iconNode = renderIcon(icon);

  const body = (
    <>
      <div className="metric-head">
        <div className="metric-title">{title}</div>
        {iconNode && <div className="metric-icon">{iconNode}</div>}
      </div>

      <div className="metric-value-box">
        <div
          className="metric-value"
          style={{ '--em': estimateEm(value) }}
          title={valueTitle != null ? String(valueTitle) : String(value)}
        >
          {display}
        </div>
      </div>

      {(trend || change) && (
        <div className="metric-foot">
          {trend && (
            <div className={`metric-trend ${trend.direction === 'down' ? 'is-down' : 'is-up'}`}>
              <ArrowIcon down={trend.direction === 'down'} />
              <span>{trend.delta}</span>
            </div>
          )}
          {change && <span className="metric-change">{change}</span>}
        </div>
      )}

      {tinted && iconNode && (
        <span className="metric-ghost" aria-hidden="true">{renderIcon(icon, { size: 96, strokeWidth: 1.5 })}</span>
      )}
    </>
  );

  const interactive = Boolean(to || href || onClick);
  const cls = [
    'metric-card',
    tinted ? 'is-tinted' : '',
    compact ? 'is-compact' : '',
    interactive ? 'is-interactive' : '',
    active ? 'is-active' : '',
    className,
  ].filter(Boolean).join(' ');
  const common = { className: cls, 'data-tone': resolvedTone };

  if (to) return <Link to={to} {...common} aria-current={active ? 'true' : undefined}>{body}</Link>;
  if (href) return <a href={href} {...common} aria-current={active ? 'true' : undefined}>{body}</a>;
  if (onClick) return <button type="button" onClick={onClick} aria-pressed={active} {...common}>{body}</button>;
  return <div {...common}>{body}</div>;
}

/* -------- Fit: how wide is this value, in em? -------- */

// Advance widths in em of the heading face as it really renders: headings ask for Plus Jakarta
// Sans, but its @font-face ranges do not cover Basic Latin (the platform ships the same files with
// the same ranges), so digits and ASCII letters come from Inter 700 with tabular figures. Measured
// in Chromium. The CSS divides the tile's width by this sum to pick a font size, so a long amount
// shrinks instead of splitting or spilling over the tile edge. An estimate: close enough for a fit;
// the 5% slack in the CSS, the 16px floor and the full amount in the tooltip cover the rest.
const GLYPH_EM = { ' ': 0.25, '\u00A0': 0.25, '.': 0.25, ',': 0.25, ':': 0.25, "'": 0.32, '-': 0.63, '/': 0.4, '%': 1 };

export function estimateEm(value) {
  let total = 0;
  Array.from(String(value ?? '')).forEach((ch) => {
    if (GLYPH_EM[ch] != null) total += GLYPH_EM[ch];
    else if (/\d/.test(ch)) total += 0.63;
    else if (/[ilIjtf!|]/.test(ch)) total += 0.3;
    else if (/[mwMWмшщжюыМШЩЖЮЫ]/.test(ch)) total += 0.88;
    else if (/\p{Lu}/u.test(ch)) total += 0.68;
    else total += 0.58;
  });
  return Math.round(total * 100) / 100;
}

/* -------- Count-up: eases the number from 0 once, the first time a real value shows -------- */

const COUNT_MS = 900;

// "1 497 000 so'm" -> { text: '1 497 000', n: 1497000, decimals: 0, sep: NBSP, suffix: " so'm" }
const NUMBER_TOKEN = /^(-?\d{1,3}(?:[  ]\d{3})+(?:\.\d+)?|-?\d+(?:\.\d+)?)/;

function splitNumber(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value)
      ? { n: value, decimals: Number.isInteger(value) ? 0 : 1, sep: '', suffix: '' }
      : null;
  }
  if (typeof value !== 'string') return null;
  const match = NUMBER_TOKEN.exec(value.trim());
  if (!match) return null;
  const text = match[1];
  const sepMatch = /[  ]/.exec(text);
  const dot = text.indexOf('.');
  return {
    n: Number(text.replace(/[  ]/g, '')),
    decimals: dot >= 0 ? text.length - dot - 1 : 0,
    sep: sepMatch ? sepMatch[0] : '',
    suffix: value.trim().slice(text.length),
  };
}

function formatStep(current, parts) {
  const fixed = Math.abs(current).toFixed(parts.decimals);
  const [whole, fraction] = fixed.split('.');
  const grouped = parts.sep ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, parts.sep) : whole;
  return `${current < 0 ? '-' : ''}${grouped}${fraction ? `.${fraction}` : ''}${parts.suffix}`;
}

// No matchMedia (jsdom, very old browsers) means the motion preference is unknown: do not animate.
function prefersReducedMotion() {
  try {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return true;
  }
}

function useCountUp(value, { enabled = true } = {}) {
  const [display, setDisplay] = useState(value);
  const animated = useRef(false);

  // A layout effect, so the zero is on screen before the first paint: with a passive effect the
  // final number flashed for a frame, then dropped to 0 and counted up.
  useLayoutEffect(() => {
    if (!enabled) {
      setDisplay(value);
      return undefined;
    }
    const parts = splitNumber(value);
    // Once per mount: the first real number counts up; a zero placeholder, a non-number, a
    // reduced-motion preference and every later value (a refresh) simply show.
    if (!parts || parts.n === 0 || animated.current || prefersReducedMotion()) {
      setDisplay(value);
      return undefined;
    }
    animated.current = true;
    let finished = false;
    let raf = null;
    const start = performance.now();
    const tick = (now) => {
      // A frame stamp can precede `start` by a fraction of a millisecond: clamp, so the first
      // frame never reads "-0.1 mln".
      const t = Math.min(1, Math.max(0, (now - start) / COUNT_MS));
      if (t < 1) {
        const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
        setDisplay(formatStep(parts.n * eased, parts));
        raf = requestAnimationFrame(tick);
      } else {
        finished = true;
        setDisplay(value); // land on the exact text the caller sent
      }
    };
    setDisplay(formatStep(0, parts));
    raf = requestAnimationFrame(tick);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      // Interrupted before it landed (a dev double-mount, a new value): allow a fresh run.
      if (!finished) animated.current = false;
    };
  }, [value, enabled]);

  return display;
}

/* -------- Icon + trend arrow -------- */

function renderIcon(icon, extra) {
  if (!icon) return null;
  if (typeof icon === 'string') {
    const Cmp = STRING_ICONS[icon] || Users;
    return <Cmp aria-hidden="true" {...(extra || {})} />;
  }
  if (extra && React.isValidElement(icon)) return React.cloneElement(icon, extra);
  return icon;
}

const ArrowIcon = ({ down }) => (
  <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    {down
      ? <path d="M1 1L13 13M13 13H5M13 13V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      : <path d="M1 13L13 1M13 1H5M13 1V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
  </svg>
);

export default MetricCard;
